import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { MRT_RowSelectionState } from "material-react-table";
import { Box, Button } from "@mui/material";

import { LOCAL_STORAGE_KEYS } from "consts";
import { EsgArrowDownWhite, LockedIcon, PlusIcon, SignedOffIcon } from "assets";
import { getApprovalsData, getApprovalsDataById, undoApprovalLock, undoApprovalSignOff, ApprovalsResponseById, ApprovalsRequestUndoBody } from "api";
import { getLoggedUserData } from "utils";
import { useNotify, usePagination, useTableFilters } from "hooks";
import { Body2Regular, CustomMaterialTable, useDialogControls, DeleteDialog, Loading, Pagination, useTableSorting, ActiveFilters, TableFilterToolbar } from "components";

import { ApprovalsDetailModal, ApprovalsAddModal } from "./components";
import { APPROVALS_MESSAGES, APPROVALS_TABLE } from "./consts";
import { APPROVALS_COLUMNS } from "./columns";
import { filterConfig, ApprovalsFilters } from "./filter-config";

export const ApprovalPage = () => {
  const notify = useNotify();
  const { sorting, onSortingChange, sortBy, sortDirection } = useTableSorting(
    APPROVALS_TABLE.COLUMN_TO_SORT_COLUMN_MAP,
    APPROVALS_TABLE.INITIAL_SORTING_STATE,
  );

  const { pagination, paginatedQuery, onPageChange, onSizeChange } = usePagination({
    localStorageKey: LOCAL_STORAGE_KEYS.APPROVALS_PAGE_PAGINATION,
    initialPage: 0,
    initialSize: 50,
  });

  // Use standardized table filters
  const tableFilters = useTableFilters<ApprovalsFilters>({
    config: filterConfig,
    pagination: { onPageChange, resetPage: () => onPageChange(1) },
  });
  const { getToolbarProps, getActiveFiltersProps, getMRTFilterProps, getQueryParams, filters } = tableFilters;
  const toolbarProps = getToolbarProps();
  const activeFiltersProps = getActiveFiltersProps();
  const mrtFilterProps = getMRTFilterProps();

  // ---- API's ----
  const { data: approvalsData, mutateAsync: fetchApprovalData, isLoading: isApprovalsLoading } = useMutation(getApprovalsData);
  const { mutateAsync: unlock } = useMutation(undoApprovalLock);
  const { mutateAsync: undoSignOff } = useMutation(undoApprovalSignOff);

  const loggedUserData = getLoggedUserData();
  const deleteAllApprovalControls = useDialogControls();
  const deleteDialogForUnlockControls = useDialogControls();
  const deleteDialogForSignOffControls = useDialogControls();

  const isDataProvider = loggedUserData?.roles?.includes("DATA_PROVIDER");
  const isDataValidator = loggedUserData?.roles?.includes("DATA_VALIDATOR");
  const isDataReviewer = loggedUserData?.roles?.includes("DATA_REVIEWER");
  const isAuditor = loggedUserData?.roles?.includes("AUDITOR");

  // ---- States ----
  const [approvalModalOpen, setApprovalModalOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<("signoff" | "lock") | "">("");
  const [showApprovalBox, setShowApprovalBox] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [signedOffIds, setSignedOffIds] = useState<ApprovalsRequestUndoBody[]>([]);
  const [lockIds, setLockIds] = useState<ApprovalsRequestUndoBody[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<ApprovalsResponseById | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);

  // ---- Const ----
  const approvalBoxRef = useRef<HTMLDivElement | null>(null);

  const tableData = useMemo(() => approvalsData?.content || [], [approvalsData?.content]);
  const tableDataDetails = detailData || [];

  const buildQueryObject = useCallback(
    (overrides = {}) => ({
      sortBy,
      sortDirection,
      ...paginatedQuery,
      ...getQueryParams(),
      ...overrides,
    }),
    [sortBy, sortDirection, paginatedQuery, getQueryParams],
  );

  const queryObject = useMemo(() => buildQueryObject(), [buildQueryObject]);

  // ---- Page Functions ----
  const handleApprovalClick = () => {
    setShowApprovalBox((prev) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (approvalBoxRef.current && !approvalBoxRef.current.contains(event.target as Node)) {
      setShowApprovalBox(false);
    }
  };

  const handlePopupClick = (statusLabel: "signoff" | "lock") => {
    setStatus(statusLabel)
    setApprovalModalOpen(true);
  }

  const handleApprovalModalClose = () => {
    setApprovalModalOpen(false);
    fetchApprovalData(queryObject);
  }

  const handleUndo = async () => {
    const mapApprovals = (approvals: ApprovalsRequestUndoBody[]) =>
      approvals.map((approval) => ({
        approvalId: approval.id,
        monthYear: approval.monthYear,
        siteName: approval.siteName,
      }));

    const executeUndo = (action: any, approvals: any[], successMsg: string, errorMsg: string, closeDialog: () => void) => {
      if (!approvals.length) return;

      action({ approvals }, {
        onSuccess: () => {
          notify.success(successMsg);
          setRowSelection({} as MRT_RowSelectionState);
          fetchApprovalData(queryObject);
          closeDialog();
        },
        onError: (e: any) => {
          notify.error(e?.response?.data || errorMsg);
          closeDialog();
        },
      })
    };

    const signedOffApprovals = mapApprovals(signedOffIds);
    const lockedApprovals = mapApprovals(lockIds);

    if (signedOffApprovals.length === 0 && lockedApprovals.length === 0) {
      notify.error("Something went wrong with approvals selected");
      return;
    }

    if (deleteDialogForSignOffControls.isOpen) {
      executeUndo(
        undoSignOff,
        signedOffApprovals,
        APPROVALS_MESSAGES.UNDO_SIGN_OFF_SUCCESS,
        APPROVALS_MESSAGES.UNDO_SIGN_OFF_ERROR,
        deleteDialogForSignOffControls.close
      );
    } else if (deleteDialogForUnlockControls.isOpen) {
      executeUndo(
        unlock,
        lockedApprovals,
        APPROVALS_MESSAGES.UNDO_LOCK_SUCCESS,
        APPROVALS_MESSAGES.UNDO_LOCK_ERROR,
        deleteDialogForUnlockControls.close
      );
    } else if (deleteAllApprovalControls.isOpen) {
      try {
        await unlock({ approvals: lockedApprovals });
        notify.success(APPROVALS_MESSAGES.UNDO_LOCK_SUCCESS);
        await undoSignOff({ approvals: signedOffApprovals });
        notify.success(APPROVALS_MESSAGES.UNDO_SIGN_OFF_SUCCESS);
        setRowSelection({} as MRT_RowSelectionState);
        fetchApprovalData(queryObject);
        deleteAllApprovalControls.close();
      } catch (e: any) {
        notify.error(e?.response?.data || "Something went wrong");
        deleteAllApprovalControls.close();
      }
    }
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      onPageChange(newPage);
      fetchApprovalData(buildQueryObject({ page: newPage - 1 }));
    },
    [onPageChange, fetchApprovalData, buildQueryObject],
  );

  const handleRowsPerPageChange = useCallback(
    (newPageSize: number) => {
      onSizeChange(newPageSize);
      fetchApprovalData(buildQueryObject({ page: 0, size: newPageSize }));
    },
    [onSizeChange, fetchApprovalData, buildQueryObject],
  );

  // ---- Use Effects ----
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection);

    const filteredSignedOff = tableData
      .filter((item: any) => selectedIds.includes(`${item.id}_${item.monthYear}_${item.siteName}`) && item.action === "SIGN_OFF");

    const filteredLock = tableData
      .filter((item: any) => selectedIds.includes(`${item.id}_${item.monthYear}_${item.siteName}`) && item.action === "LOCK");

    setSignedOffIds(filteredSignedOff || []);
    setLockIds(filteredLock || []);

  }, [rowSelection, tableData]);

  useEffect(() => {
    if (showApprovalBox) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showApprovalBox]);

  useEffect(() => {
    if (!sortBy) return;
    fetchApprovalData(buildQueryObject());
  }, [sortBy, sortDirection, buildQueryObject, fetchApprovalData]);

  // Refetch data when filters change (same pattern as useSitesPaginated reacts to filter changes)
  useEffect(() => {
    fetchApprovalData(buildQueryObject());
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ mt: 3, mx: 3 }}>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", alignItems: "center" }}>
        <Box
          ref={approvalBoxRef}
          sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "220px", position: "relative" }}>
            {/* Approval Actions Button */}
            <Button
              sx={{
                borderRadius: 2,
                color: "white",
                width: "220px",
                height: "36px"
              }}
              variant="contained"
              endIcon={<EsgArrowDownWhite />}
              startIcon={<PlusIcon />}
              disabled={isDataProvider || isAuditor || isDataReviewer}
              onClick={handleApprovalClick}>
              <Body2Regular>Add Approval Actions</Body2Regular>
            </Button>
            {/* Conditional Box */}
            {showApprovalBox && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  mt: 1,
                  p: 1,
                  border: "1px solid #C1C1C1",
                  borderRadius: "8px",
                  backgroundColor: "#FBFBFB",
                  zIndex: 10,
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Sign off Button */}
                <Button
                  sx={{
                    borderRadius: 2,
                    color: "white",
                    textTransform: "none",
                    mb: "8px",
                    width: "200px",
                    height: "36px",
                  }}
                  variant="contained"
                  startIcon={<SignedOffIcon color="white" />}
                  onClick={() => handlePopupClick("signoff")}
                >
                  <Body2Regular>Sign off</Body2Regular>
                </Button>
                {/* Lock Button */}
                <Button
                  sx={{
                    borderRadius: 2,
                    color: "white",
                    textTransform: "none",
                    width: "200px",
                    height: "36px",
                  }}
                  onClick={() => handlePopupClick("lock")}
                  variant="contained"
                  disabled={isDataValidator}
                  startIcon={<LockedIcon color="white" />}
                >
                  <Body2Regular>Lock</Body2Regular>
                </Button>
              </Box>
            )}
          </Box>
          {(signedOffIds?.length > 0 && lockIds?.length === 0) && <Button
            sx={{
              borderRadius: 2,
              color: "white",
              textTransform: "none",
              width: "180px",
              height: "36px",
              bgcolor: "#0E25D5"
            }}
            variant="contained"
            startIcon={<SignedOffIcon color="white" />}
            onClick={deleteDialogForSignOffControls.open}
            disabled={isDataProvider || isAuditor || isDataReviewer}
          >
            <Body2Regular>Undo Sign-off</Body2Regular>
          </Button>}
          {(lockIds?.length > 0 && signedOffIds?.length === 0) && <Button
            sx={{
              borderRadius: 2,
              color: "white",
              textTransform: "none",
              width: "180px",
              height: "36px",
              bgcolor: "#000"
            }}
            variant="contained"
            startIcon={<SignedOffIcon color="white" />}
            onClick={deleteDialogForUnlockControls.open}
            disabled={isDataProvider || isAuditor || isDataReviewer || isDataValidator}
          >
            <Body2Regular>Unlock</Body2Regular>
          </Button>}
          {(lockIds?.length > 0 && signedOffIds?.length > 0) && <Button
            sx={{
              borderRadius: 2,
              color: "white",
              textTransform: "none",
              width: "280px",
              height: "36px",
              bgcolor: "#DD1515"
            }}
            variant="contained"
            startIcon={<SignedOffIcon color="white" />}
            onClick={deleteAllApprovalControls.open}
            disabled={isDataProvider || isAuditor || isDataReviewer || isDataValidator}
          >
            <Body2Regular>Undo All Selected Approvals</Body2Regular>
          </Button>}
        </Box>
        <Box sx={{ marginTop: "10px" }}>
          <TableFilterToolbar {...toolbarProps} />
        </Box>
      </Box>
      {isApprovalsLoading ? <Loading />
        : <Box sx={{ mt: 3 }}>
          <ActiveFilters
            {...activeFiltersProps}
            onClearFilter={(filterId) => {
              activeFiltersProps.onClearFilter(filterId);
              // Manually trigger refetch since clearFilter doesn't use callbacks
              fetchApprovalData(buildQueryObject({ [filterId]: null, page: 0 }));
            }}
          />
          <CustomMaterialTable
            state={{ isLoading: isApprovalsLoading, rowSelection, sorting }}
            data={tableData}
            columns={APPROVALS_COLUMNS}
            getRowId={(row) => `${row.id}_${row.monthYear}_${row.siteName}`}
            enableSorting
            manualSorting
            enablePagination
            manualPagination
            rowCount={approvalsData?.totalElements || 0}
            enableGlobalFilter={false}
            onSortingChange={onSortingChange}
            {...mrtFilterProps}
            initialState={{
              density: 'compact',
              ...mrtFilterProps.initialState,
            }}
            enableTopToolbar={false}
            enableRowSelection={(row: any) => row}
            onRowSelectionChange={setRowSelection}
            enableStickyHeader
            containerSx={{
              height: "calc(100vh - 288px)",
            }}
            rowClickable
            muiTableBodyRowProps={({ row }) => ({
              onClick: async () => {
                const { id: approvalId, monthYear, siteName } = row.original;
                if (approvalId) {
                  const approvalsDetails = await getApprovalsDataById({ id: approvalId, monthYear, siteName });
                  setDetailData(approvalsDetails);
                  setActionId(approvalId);
                }
                setOpen(true);
              },
            })}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Pagination
              page={pagination.page + 1}
              rowsCount={approvalsData?.totalElements || 0}
              rowsPerPage={pagination.size}
              rowsPerPageOptions={[50, 100, 200]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Box>}
      <ApprovalsAddModal
        isOpen={approvalModalOpen}
        onClose={handleApprovalModalClose}
        status={status}
      />
      <DeleteDialog
        isOpen={deleteDialogForSignOffControls.isOpen || deleteDialogForUnlockControls.isOpen || deleteAllApprovalControls.isOpen}
        onCancel={() => {
          deleteDialogForSignOffControls.close();
          deleteDialogForUnlockControls.close();
          deleteAllApprovalControls.close();
        }}
        onDeleteClick={handleUndo}
        target="Approvals"
        confirmTitle="Confirm"
        title="Undo All Selected Approvals"
      />
      {actionId && <ApprovalsDetailModal
        open={open}
        setOpen={setOpen}
        detailData={tableDataDetails}
        actionId={actionId}
      />}
    </Box>
  );
};

export default ApprovalPage;
